/**
 * The first sections of `cycle-step.html` for step 3 (f10.6, design D10): the
 * drop zone (with the fixed-unit select for the DB scope), "File dropped" and
 * "What the system did with it". Every fact is the batch's own.
 */

import { useRef, useState } from 'react';
import { AlertCircle, CheckCircle2, FileSpreadsheet, Repeat, UploadCloud } from 'lucide-react';

import type { KimbleReview } from '@/api/client';
import { formatBytes, formatWhen, shortChecksum, statePresentation } from '@/routes/data-import/importView';
import { EmptyCell } from '@/ui/EmptyCell';

import { currencySummary, formatNote, severityPill, transformations } from './kimbleDumpView';

function useFilePicker(onFile: (file: File) => void, label: string) {
  const input = useRef<HTMLInputElement>(null);
  const element = (
    <input
      ref={input}
      type="file"
      className="sr-only"
      aria-label={label}
      onChange={(event) => {
        const file = event.target.files?.item(0);
        if (file) onFile(file);
        event.target.value = '';
      }}
    />
  );
  return { open: () => input.current?.click(), element };
}

export interface UnitOption {
  id: string;
  name: string;
}

export function KimbleDumpDropzone({
  scopeName,
  onFile,
  busy,
  unitOptions,
  unitId,
  onUnitChange,
}: {
  scopeName: string;
  onFile: (file: File) => void;
  busy: boolean;
  /** Only for the fixed-unit scope: the unit every row belongs to. */
  unitOptions?: UnitOption[];
  unitId?: string;
  onUnitChange?: (unitId: string) => void;
}) {
  const [over, setOver] = useState(false);
  const picker = useFilePicker(onFile, `Kimble dump file for ${scopeName}`);
  const needsUnit = unitOptions !== undefined;
  const blocked = busy || (needsUnit && !unitId);
  return (
    <div className="stack">
      {needsUnit && (
        <div className="field">
          <label htmlFor="kimble-fixed-unit">
            Business Unit
          </label>
          <select
            id="kimble-fixed-unit"
            value={unitId ?? ''}
            onChange={(event) => onUnitChange?.(event.target.value)}
            required
          >
            <option value="">Choose the unit every row of this dump belongs to…</option>
            {unitOptions.map((unit) => (
              <option key={unit.id} value={unit.id}>
                {unit.name}
              </option>
            ))}
          </select>
          <div className="hint">This export has no Proposal: Unit column, so its unit is set here.</div>
        </div>
      )}
      <div
        className={`dropzone${over ? ' is-over' : ''}`}
        onDragOver={(event) => {
          event.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(event) => {
          event.preventDefault();
          setOver(false);
          const file = event.dataTransfer.files.item(0);
          if (file && !blocked) onFile(file);
        }}
      >
        <UploadCloud className="dz-icon" aria-hidden="true" />
        <div className="dz-title">Drop the {scopeName} Kimble dump</div>
        <div className="hint">
          One row per resource, element and month. Columns are read by header name; every amount keeps its own
          currency.
        </div>
        <div className="btn-row dz-actions">
          <button type="button" className="btn btn-primary" disabled={blocked} onClick={picker.open}>
            <UploadCloud aria-hidden="true" /> Choose file
          </button>
        </div>
        <div className="hint">The format is detected from the file&rsquo;s content, never from its name</div>
        {picker.element}
      </div>
    </div>
  );
}

export function KimbleDumpFileCard({
  review,
  onReplace,
  busy,
}: {
  review: KimbleReview;
  onReplace: (file: File) => void;
  busy: boolean;
}) {
  const { batch } = review;
  const picker = useFilePicker(onReplace, 'Replacement Kimble dump file');
  const state = statePresentation(batch.state.code);
  const staged = batch.state.code === 'STAGED' || batch.state.code === 'AWAITING_RESOLUTION';
  return (
    <div className="card">
      <div className="card-head">
        <h3>File dropped</h3>
        {batch.state.code === 'COMMITTED' ? (
          <span className="badge pill-success">{batch.is_current ? 'Committed — current dump' : 'Committed'}</span>
        ) : staged ? (
          <span className="badge pill-info">Staged — not committed</span>
        ) : (
          <span className={`badge ${state.pill}`}>{state.label}</span>
        )}
      </div>
      <div className="card-body">
        <div className="dropzone is-filled">
          <div className="row us-eng-file">
            <FileSpreadsheet className="dz-icon" aria-hidden="true" />
            <div className="us-eng-file-text">
              <div className="dz-title truncate">{batch.original_filename ?? 'The uploaded file'}</div>
              <div className="hint">
                {formatBytes(batch.byte_size)} &middot; dropped {formatWhen(batch.uploaded_at)}
                {batch.uploaded_by ? ` by ${batch.uploaded_by.name}` : ''} &middot; batch #{batch.batch_number}
              </div>
            </div>
            <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={picker.open}>
              <Repeat aria-hidden="true" /> Replace
            </button>
          </div>
          {picker.element}
        </div>
      </div>
    </div>
  );
}

export function KimbleDumpProcessingCard({ review }: { review: KimbleReview }) {
  const { batch, issues, figures } = review;
  const note = formatNote(review);
  return (
    <div className="card">
      <div className="card-head">
        <h3>What the system did with it</h3>
        <span className="hint">
          {issues.counts.ERROR ?? 0} error &middot; {issues.counts.WARNING ?? 0} warning &middot;{' '}
          {issues.counts.INFO ?? 0} info
        </span>
      </div>
      <div className="card-body stack">
        <dl className="dl cols-4">
          <div>
            <dt>Detected format</dt>
            <dd>
              {batch.detected_format} <span className="hint">{note ?? 'sniffed by content'}</span>
            </dd>
          </div>
          <div>
            <dt>Header row</dt>
            <dd>{batch.detected_header_row === null ? <EmptyCell /> : batch.detected_header_row}</dd>
          </div>
          <div>
            <dt>Scope and unit</dt>
            <dd>
              {batch.scope?.name ?? '—'}
              {batch.business_unit ? <span className="hint"> &middot; {batch.business_unit.name}</span> : null}
            </dd>
          </div>
          <div>
            <dt>Currencies</dt>
            <dd>{currencySummary(figures.currencies)}</dd>
          </div>
          <div>
            <dt>Rows parsed / staged / dropped</dt>
            <dd>
              {batch.rows_in ?? '—'} / {batch.rows_out ?? '—'} / {batch.rows_dropped ?? '—'}
            </dd>
          </div>
          <div>
            <dt>Checksum</dt>
            <dd className="mono">{shortChecksum(batch.content_sha256)}</dd>
          </div>
        </dl>

        <div>
          <div className="stat-label">Transformations applied</div>
          <ul className="stack-sm us-eng-list">
            {transformations(review).map((item) => (
              <li key={item.text} className="row us-eng-transform">
                {item.ok ? (
                  <CheckCircle2 className="us-eng-ok" aria-hidden="true" />
                ) : (
                  <AlertCircle className="us-eng-warn" aria-hidden="true" />
                )}
                <span>{item.text}</span>
              </li>
            ))}
          </ul>
        </div>

        {issues.items.length > 0 && (
          <div>
            <div className="stat-label">Issues</div>
            <ul className="stack-sm us-eng-list">
              {issues.items.map((item, index) => (
                <li key={`${item.code}-${index}`} className="row us-eng-issue">
                  <span className={severityPill(item.severity)}>{item.severity}</span>
                  <span className="mono">{item.code}</span>
                  <span className="us-eng-issue-text">
                    {item.message}
                    {item.source_row_number !== null && (
                      <span className="hint"> &middot; row {item.source_row_number}</span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
